import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowLeft, Check, ChevronLeft, Lightbulb } from 'lucide-react';
import { useEvent } from 'context/events/Event';
import ticketsApi from 'api/requests/tickets';
import Loader from 'components/_UI/Loader';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import useMediaQuery from 'hooks/useMediaQuery';
import useFeeSimulation from 'hooks/useFeeSimulation';
import currency from 'helpers/currency';
import eventTypes from 'theme/eventTypes';
import breakpoints from 'theme/breakpoints';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import TicketList from './TicketList';
import TicketEditor from './TicketEditor';
import {
    cloneTicket,
    eventTotals,
    pricesToSimulate,
    nameWithSuffix,
    newTicket,
    ticketFromServer,
    ticketIssues,
    ticketPayload,
    ticketSignature,
} from './ticketModel';
import { ERROR, INK, INK_SOFT, LINE, MUTED, accent, accentAlpha } from './ticketFormStyles';

/**
 * Etapa de ingressos do assistente do evento: a lista de ingressos à esquerda e
 * o editor do selecionado à direita (no celular, uma tela de cada vez).
 *
 * Nada vai para a API até "Salvar" — trocar de ingresso, voltar à lista ou
 * reordenar só mexe no estado daqui, e por isso nenhuma dessas ações pergunta
 * se o produtor quer descartar o que fez.
 */
export default function TicketForm({
    availableProducts,
    isDraft,
    onSuccess = () => false,
    goBackFormStep = () => false
}) {
    const { event } = useEvent();
    const { toasts, toast } = useToast();
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.md})`);

    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [mobileView, setMobileView] = useState('list');
    const [deletedTickets, setDeletedTickets] = useState([]);
    const [deletedBatches, setDeletedBatches] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [submitErrorMsg, setSubmitErrorMsg] = useState('');
    const [revealKey, setRevealKey] = useState(0);

    // Como cada ingresso chegou do servidor, para marcar o que mudou.
    const savedRef = useRef({ byId: new Map(), order: '' });
    const editorRef = useRef(null);

    const isEventFree = event?.classification?.id === Number(eventTypes.free.id);
    const isEdit = event?.products?.some(product => product.type === 'ticket');
    // Products attached to the event share the tickets' absorbed value (all-or-nothing per event).
    const eventProducts = (event?.products || []).filter(product => product?.type === 'product');
    const absorbed = tickets[0]?.absorbed_service_fee ?? false;

    // A taxa é calculada pelo backend (estimativa para um pedido de um ingresso):
    // uma requisição só para o preço de todos os lotes de todos os ingressos.
    const { ready: feeReady, feeFor } = useFeeSimulation(pricesToSimulate(tickets, { isEventFree, absorbed }));

    useEffect(() => {
        const loaded = isEdit
            ? event.products.filter(product => product?.type === 'ticket').map(ticketFromServer)
            : [newTicket()];

        savedRef.current = isEdit
            ? {
                byId: new Map(loaded.map(ticket => [ticket.internal_id, ticketSignature(ticket, { isEventFree })])),
                order: loaded.map(ticket => ticket.internal_id).join(),
            }
            : { byId: new Map(), order: '' };

        setTickets(loaded);
        setSelectedId(loaded[0]?.internal_id ?? null);
        // Evento novo abre direto no editor do primeiro ingresso: não há
        // escolha a fazer numa lista de um item só.
        setMobileView(isEdit ? 'list' : 'editor');
    }, [event]);

    const issuesById = useMemo(() => {
        const map = new Map();
        tickets.forEach(ticket => map.set(ticket.internal_id, ticketIssues(ticket, { isEventFree })));
        return map;
    }, [tickets, isEventFree]);

    const dirtyIds = useMemo(() => {
        if (!isEdit) return new Set();

        return new Set(tickets
            .filter(ticket => savedRef.current.byId.get(ticket.internal_id) !== ticketSignature(ticket, { isEventFree }))
            .map(ticket => ticket.internal_id));
    }, [tickets, isEdit, isEventFree]);

    const orderChanged = isEdit && tickets.map(ticket => ticket.internal_id).join() !== savedRef.current.order
        && tickets.length === savedRef.current.byId.size;
    const changeCount = dirtyIds.size + deletedTickets.length + (orderChanged ? 1 : 0);

    const totals = eventTotals(tickets, { isEventFree, absorbed, feeFor });
    const ticketsWithIssues = tickets.filter(ticket => issuesById.get(ticket.internal_id)?.length);
    const issueTotal = ticketsWithIssues.reduce((total, ticket) => total + issuesById.get(ticket.internal_id).length, 0);
    const selectedIndex = tickets.findIndex(ticket => ticket.internal_id === selectedId);
    const selected = tickets[selectedIndex];

    // Resolvidas as pendências, a tentativa de salvar deixa de valer: sem isso,
    // um lote ou ingresso criado depois já nasceria todo vermelho.
    useEffect(() => {
        if (submitAttempted && !ticketsWithIssues.length) setSubmitAttempted(false);
    }, [submitAttempted, ticketsWithIssues.length]);

    function updateTicket(internalId, updater) {
        setTickets(previous => previous.map(ticket => (
            ticket.internal_id === internalId ? updater(ticket) : ticket
        )));
    }

    function selectTicket(internalId) {
        setSelectedId(internalId);
        setMobileView('editor');
    }

    function addTicket(ticket = { ...newTicket(), absorbed_service_fee: absorbed }) {
        setTickets(previous => [...previous, ticket]);
        selectTicket(ticket.internal_id);
    }

    function duplicateTicket() {
        addTicket(cloneTicket(selected, { name: nameWithSuffix(selected.name, ' (cópia)') }));
    }

    function createHalfTicket() {
        addTicket(cloneTicket(selected, { name: nameWithSuffix(selected.name, ' – Meia'), priceFactor: 0.5 }));
        toast.success('Meia-entrada criada com metade do preço em todos os lotes. Confira os valores.');
    }

    function moveTicket(from, to) {
        if (to < 0 || to >= tickets.length) return;

        setTickets(previous => {
            const next = [...previous];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    }

    function confirmDeleteTicket() {
        const removed = tickets.find(ticket => ticket.internal_id === deleteConfirm);
        const remaining = tickets.filter(ticket => ticket.internal_id !== deleteConfirm);

        if (removed?.id) {
            setDeletedTickets(previous => [...previous, removed.id]);
            setDeletedBatches(previous => [
                ...previous,
                ...removed.batches.filter(batch => batch.id ?? false).map(batch => batch.id),
                ...(removed.batch_id ? [removed.batch_id] : []),
            ]);
        }

        setTickets(remaining);
        setSelectedId(remaining[Math.max(selectedIndex - 1, 0)]?.internal_id ?? null);
        setMobileView('list');
        setDeleteConfirm(null);
    }

    // A taxa vale para o evento inteiro — ingressos e produtos. Por isso ela
    // mora aqui, no topo da etapa, e não dentro de um ingresso.
    function setAbsorbed(value) {
        setTickets(previous => previous.map(ticket => ({ ...ticket, absorbed_service_fee: value })));
    }

    function goToFirstIssue() {
        const first = ticketsWithIssues[0];

        if (!first) return;

        setSelectedId(first.internal_id);
        setMobileView('editor');
        setRevealKey(key => key + 1);

        // Espera o editor do ingresso montar (e "Mais opções" abrir) para achar o campo.
        setTimeout(() => {
            const field = editorRef.current?.querySelector(
                '[data-invalid="true"] input, [data-invalid="true"] textarea'
            );
            field?.focus();
            field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 150);
    }

    function onSubmit() {
        if (ticketsWithIssues.length) {
            setSubmitAttempted(true);
            goToFirstIssue();
            return;
        }

        const hasInactiveBatchWithData = tickets.some(ticket =>
            ticket.batches.some(batch => !batch.is_active && (batch.floatPrice > 0 || batch.quantity))
        );

        if (hasInactiveBatchWithData) {
            toast.warning('Alguns lotes com dados preenchidos estão inativos.');
        }

        if (isEdit) {
            editTickets();
        } else {
            createTickets();
        }
    }

    function buildPayload(withIds) {
        return {
            type: 'tickets',
            event_id: event?.id,
            products: tickets.map(ticket => ticketPayload(ticket, { eventId: event?.id, isEventFree, withIds })),
        };
    }

    function failSubmit(message) {
        toast.error(message);
        setSubmitErrorMsg('Falha ao salvar. Verifique sua conexão e tente novamente.');
        setTimeout(() => setSubmitErrorMsg(''), 5000);
    }

    async function createTickets() {
        try {
            setLoading(true);

            const { data } = await ticketsApi.createBulk(buildPayload(false));
            onSuccess({
                ...data,
                products: availableProducts
            });

            if (!availableProducts.length && !isDraft) {
                removeDraft();
            }
        } catch (error) {
            failSubmit('Erro ao criar ingressos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    async function editTickets() {
        try {
            setLoading(true);

            if (deletedTickets.length || deletedBatches.length) {
                const deletePayload = {};

                if (deletedTickets.length) {
                    deletePayload.productsToDelete = deletedTickets;
                }

                if (deletedBatches.length) {
                    deletePayload.batchesToDelete = deletedBatches;
                }

                await ticketsApi.deleteBulk({
                    data: deletePayload
                });
            }

            // The backend normalizes the absorbed value across all of the event's products,
            // so the tickets bulk call alone keeps the whole event consistent.
            await ticketsApi.updateBulk(buildPayload(true));

            onSuccess();

            if (!availableProducts.length && !isDraft) {
                removeDraft();
            }
        } catch (error) {
            failSubmit('Erro ao atualizar ingressos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    async function removeDraft() {
        try {
            await ticketsApi.removeDraft(event?.slug);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }

    const showList = !isMobile || mobileView === 'list';
    const showEditor = selected && (!isMobile || mobileView === 'editor');
    const showTip = !isEdit && tickets.length === 1 && !isEventFree && !tickets[0].is_free;
    const netLabel = absorbed && !feeReady ? '—' : currency.format(totals.net);

    return (
        <FormContainer>
            <ToastContainer toasts={toasts} />

            {showList && (
                <PageHeader>
                    <PageTitle>{isEdit ? 'Editar ingressos' : 'Ingressos'}</PageTitle>
                    <PageSubtitle>Configure os tipos de ingresso e os lotes de cada um.</PageSubtitle>
                </PageHeader>
            )}

            {showList && !isEventFree && (
                <FeeBand aria-labelledby='fee-band-title'>
                    <FeeText>
                        <FeeTitle id='fee-band-title'>Taxa de serviço</FeeTitle>
                        <FeeHint>
                            Vale para todos os ingressos{eventProducts.length ? ' e produtos' : ''} deste evento.
                        </FeeHint>
                    </FeeText>
                    <Segmented role='radiogroup' aria-labelledby='fee-band-title'>
                        <Segment type='button' role='radio' aria-checked={!absorbed} onClick={() => setAbsorbed(false)}>
                            Comprador paga
                        </Segment>
                        <Segment type='button' role='radio' aria-checked={absorbed} onClick={() => setAbsorbed(true)}>
                            Eu absorvo
                        </Segment>
                    </Segmented>
                </FeeBand>
            )}

            <Layout>
                {showList && (
                    <Aside>
                        <TicketList
                            tickets={tickets}
                            selectedId={isMobile ? null : selectedId}
                            isEventFree={isEventFree}
                            dirtyIds={dirtyIds}
                            issueCountOf={ticket => {
                                const count = issuesById.get(ticket.internal_id)?.length || 0;
                                return submitAttempted ? count : 0;
                            }}
                            onSelect={selectTicket}
                            onAdd={() => addTicket()}
                            onMove={moveTicket}
                        />

                        {showTip && (
                            <Tip>
                                <Lightbulb size={18} aria-hidden='true' />
                                <p>
                                    <strong>Vai ter meia-entrada?</strong> Preencha este ingresso e use{' '}
                                    <strong>Criar meia-entrada</strong>: ele é duplicado com metade do preço em todos os lotes.
                                </p>
                            </Tip>
                        )}
                    </Aside>
                )}

                {showEditor && (
                    <EditorColumn ref={editorRef}>
                        {isMobile && (
                            <BackToList type='button' onClick={() => setMobileView('list')}>
                                <ChevronLeft size={20} aria-hidden='true' />
                                Ingressos
                            </BackToList>
                        )}
                        <TicketEditor
                            key={selected.internal_id}
                            ticket={selected}
                            onChange={updater => updateTicket(selected.internal_id, updater)}
                            isEventFree={isEventFree}
                            absorbed={absorbed}
                            feeFor={feeFor}
                            issues={issuesById.get(selected.internal_id) || []}
                            submitAttempted={submitAttempted}
                            revealKey={revealKey}
                            canMoveUp={selectedIndex > 0}
                            canMoveDown={selectedIndex < tickets.length - 1}
                            canDelete={tickets.length > 1}
                            onMoveUp={() => moveTicket(selectedIndex, selectedIndex - 1)}
                            onMoveDown={() => moveTicket(selectedIndex, selectedIndex + 1)}
                            onDuplicate={duplicateTicket}
                            onCreateHalf={createHalfTicket}
                            onDelete={() => setDeleteConfirm(selected.internal_id)}
                            onDeleteBatchId={id => setDeletedBatches(previous => [...previous, id])}
                        />
                    </EditorColumn>
                )}
            </Layout>

            <ConfirmDeleteModal
                isOpen={deleteConfirm !== null}
                title='Excluir ingresso'
                message='Tem certeza que deseja excluir este ingresso? Esta ação não pode ser desfeita.'
                onConfirm={confirmDeleteTicket}
                onCancel={() => setDeleteConfirm(null)}
            />

            <BottomSpacer />

            <BottomBar>
                {submitAttempted && ticketsWithIssues.length > 0 && (
                    <IssueBanner role='alert'>
                        <AlertCircle size={16} aria-hidden='true' />
                        <p>
                            {ticketsWithIssues.length === 1
                                ? <>Corrija {issueTotal === 1 ? '1 campo' : `${issueTotal} campos`} em <strong>“{ticketsWithIssues[0].name || 'Ingresso sem nome'}”</strong> para salvar.</>
                                : <>Corrija {issueTotal} campos em {ticketsWithIssues.length} ingressos para salvar.</>}
                        </p>
                        <BannerAction type='button' onClick={goToFirstIssue}>Ir para o primeiro</BannerAction>
                    </IssueBanner>
                )}

                {submitErrorMsg && (
                    <IssueBanner role='alert'>
                        <AlertCircle size={16} aria-hidden='true' />
                        <p>{submitErrorMsg}</p>
                    </IssueBanner>
                )}

                <BarContent>
                    {!(isMobile && mobileView === 'editor') && (
                        <Totals>
                            {isEdit ? (
                                <Total>
                                    <dt>Vendidos</dt>
                                    <dd>{totals.committed} de {totals.capacity}</dd>
                                </Total>
                            ) : (
                                <Total>
                                    <dt>Ingressos</dt>
                                    <dd>{totals.capacity}</dd>
                                </Total>
                            )}
                            {!isMobile && (
                                <Total>
                                    <dt>{isEdit ? 'Receita bruta projetada' : 'Receita bruta'}</dt>
                                    <dd>{currency.format(totals.gross)}</dd>
                                </Total>
                            )}
                            <Total>
                                <dt>Líquida projetada</dt>
                                <dd>{netLabel}</dd>
                            </Total>
                        </Totals>
                    )}

                    <BarActions>
                        {changeCount > 0 && (
                            <Unsaved>
                                <span aria-hidden='true' />
                                {changeCount === 1 ? '1 alteração não salva' : `${changeCount} alterações não salvas`}
                            </Unsaved>
                        )}
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                {!(isMobile && mobileView === 'editor') && (
                                    <BackButton type='button' onClick={() => goBackFormStep(1)}>
                                        <ArrowLeft size={16} aria-hidden='true' />
                                        Voltar
                                    </BackButton>
                                )}
                                <SubmitButton type='button' onClick={onSubmit}>
                                    <Check size={16} aria-hidden='true' />
                                    {isEdit ? 'Salvar alterações' : 'Criar ingressos'}
                                </SubmitButton>
                            </>
                        )}
                    </BarActions>
                </BarContent>
            </BottomBar>
        </FormContainer>
    );
}

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    color: ${INK};
`;

const PageHeader = styled.header`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PageTitle = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.2px;
    color: ${INK};
`;

const PageSubtitle = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${MUTED};
`;

const FeeBand = styled.section`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
    padding: 14px 18px;
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: 14px;
`;

const FeeText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 220px;
`;

const FeeTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${INK};
`;

const FeeHint = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${MUTED};
`;

const Segmented = styled.div`
    display: flex;
    gap: 4px;
    padding: 4px;
    background: #F0EFF5;
    border-radius: 12px;
`;

const Segment = styled.button`
    min-height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: #4A4A52;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &[aria-checked='true'] {
        background: #fff;
        color: ${accent};
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

const Layout = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 20px;
`;

const Aside = styled.div`
    flex: 1 1 300px;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (max-width: ${breakpoints.md}) {
        max-width: none;
    }
`;

const EditorColumn = styled.div`
    flex: 999 1 520px;
    min-width: 0;
`;

const Tip = styled.div`
    display: flex;
    gap: 10px;
    padding: 14px;
    border-radius: 14px;
    background: #F7EFFF;
    border: 1px dashed #DFC8F8;
    color: #3A2A55;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
        color: #6D14CC;
    }

    p {
        margin: 0;
        font-size: 13px;
        line-height: 1.45;
    }

    strong:first-child {
        color: #6D14CC;
    }
`;

const BackToList = styled.button`
    min-height: 44px;
    margin-left: -8px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${accent};
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
`;

const BottomSpacer = styled.div`
    height: 120px;
`;

const BottomBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    border-top: 1px solid ${LINE};
    box-shadow: 0 -4px 16px rgba(20, 10, 50, 0.06);
    z-index: 10;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 75%;
    }
`;

const IssueBanner = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    padding: 8px 24px;
    background: #FCEEEE;
    border-bottom: 1px solid #F1C4C1;
    color: #8C1D18;

    svg {
        flex-shrink: 0;
        color: ${ERROR};
    }

    p {
        flex: 1 1 200px;
        margin: 0;
        font-size: 13px;
    }
`;

const BannerAction = styled.button`
    min-height: 36px;
    padding: 0 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${ERROR};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #F8DCDB;
    }
`;

const BarContent = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 20px;
    padding: 12px 24px;

    @media screen and (max-width: ${breakpoints.sm}) {
        padding: 12px 16px 16px;
    }
`;

const Totals = styled.dl`
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 28px;
`;

const Total = styled.div`
    dt {
        font-size: 12px;
        color: ${MUTED};
    }

    dd {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: ${INK};
    }
`;

const BarActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 12px;

    @media screen and (max-width: ${breakpoints.sm}) {
        width: 100%;

        & > button {
            flex: 1 1 auto;
        }
    }
`;

const Unsaved = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${INK_SOFT};

    span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${accent};
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
`;

const BackButton = styled.button`
    min-height: 44px;
    padding: 0 18px;
    border: 1px solid #D9D9E0;
    border-radius: 10px;
    background: #fff;
    color: ${INK};
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;

    &:hover {
        background: #F6F5FA;
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

const SubmitButton = styled.button`
    min-height: 44px;
    padding: 0 22px;
    border: 0;
    border-radius: 10px;
    background: ${accent};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;

    &:hover {
        background: ${accentAlpha('E6')};
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;
